/**
 * Stepper — mobile-first, slim progress indicator used by the
 * Onboarding wizard (and reusable by any other multi-step flow).
 *
 * Visual model:
 *   • Mobile (default): a single slim progress *bar* + "Paso N de
 *     TOTAL" label. Takes 36px vertical space, leaves all the screen
 *     for the form.
 *   • Desktop (md+): horizontal *dot* track. Each dot has its label
 *     underneath. The active dot is filled, completed dots show a
 *     check, future dots stay outlined.
 *
 * Why two presentations:
 *   On mobile a dot row with 6 labels would either truncate or wrap.
 *   A progress bar conveys the same "I'm at step N/6" information
 *   without robbing vertical real estate from the thumb-reachable
 *   form area. Desktop has the budget to show all labels at once, so
 *   we do.
 *
 * Props:
 *   • `current` (1-indexed) · which step is active.
 *   • `total` · total number of steps (must match `labels.length`).
 *   • `labels` · array of short strings (one per step).
 *   • `onStepClick(stepIndex)` · optional. When provided, completed
 *     dots become clickable so the user can jump back. Future dots
 *     stay locked.
 */
import { Check } from "lucide-react";
import { cn } from "../../lib/utils";

export function Stepper({ current, total, labels = [], onStepClick }) {
  const safeTotal = Math.max(1, total || labels.length || 1);
  const percent = Math.min(100, Math.max(0, ((current - 1) / (safeTotal - 1)) * 100));

  return (
    <div className="w-full" data-testid="onboarding-stepper">
      {/* Mobile · slim bar + counter. md:hidden hides the bar on desktop. */}
      <div className="md:hidden flex items-center gap-3 px-4 py-3" aria-hidden={false}>
        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-600 transition-all duration-500 ease-out"
            style={{ width: `${Math.max(8, ((current) / safeTotal) * 100)}%` }}
            data-testid="onboarding-stepper-progress-mobile"
          />
        </div>
        <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 shrink-0">
          {current} / {safeTotal}
        </span>
      </div>

      {/* Desktop · horizontal dot track. */}
      <div className="hidden md:flex items-start justify-between gap-2 px-2 py-4">
        {labels.map((label, i) => {
          const step = i + 1;
          const state = step < current ? "done" : step === current ? "active" : "pending";
          const clickable = state === "done" && onStepClick;
          return (
            <button
              key={i}
              type="button"
              onClick={clickable ? () => onStepClick(step) : undefined}
              disabled={!clickable}
              data-testid={`onboarding-stepper-step-${step}`}
              className={cn(
                "group flex flex-col items-center gap-1.5 flex-1 min-w-0",
                clickable ? "cursor-pointer" : "cursor-default",
              )}
            >
              <span
                className={cn(
                  "h-8 w-8 rounded-full inline-flex items-center justify-center text-xs font-bold border-2 transition-all",
                  state === "done"   && "bg-emerald-600 border-emerald-600 text-white",
                  state === "active" && "bg-white border-slate-900 text-slate-900 ring-4 ring-slate-900/10",
                  state === "pending"&& "bg-white border-slate-300 text-slate-400",
                )}
              >
                {state === "done" ? <Check className="h-4 w-4" /> : step}
              </span>
              <span
                className={cn(
                  "text-[11px] leading-tight text-center px-0.5",
                  state === "active" && "font-semibold text-slate-900",
                  state === "done"   && "text-slate-600",
                  state === "pending"&& "text-slate-400",
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Desktop · thin connector line under the dots. Decorative only. */}
      <div className="hidden md:block relative h-px -mt-7 mx-12 pointer-events-none">
        <div className="absolute inset-0 bg-slate-200" />
        <div
          className="absolute inset-y-0 left-0 bg-emerald-600 transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
